import React, {
  useEffect,
  useContext,
  useRef,
  useLayoutEffect,
  useMemo,
} from "react";
import camelcaseKeys from "camelcase-keys";
import { useState } from "react";
import {
  CasticulateAssessment,
  CasticulateAssessmentCheckbox,
  CasticulateAssessmentLongAnswer,
  CasticulateEndSlide,
  CasticulateExternalLink,
  CasticulateImageCarousel,
  CasticulateNavSlide,
  CasticulateResultsSlide,
  CasticulateSurvey,
  CasticulateTitleSlide,
  CasticulateVideoResource,
  CasticulateImageResource,
  ResourceCompleteModal,
  CasticulateAudioResource,
  CasticulatePdfResource,
  CasticulateAssessmentPoll,
  CasticulatePPST,
  Modal,
} from "../../../organisms/components";
import {
  useCasticulateViewScreenQuery,
  CasticulateNode as CasticulateNodeOriginal,
  CasticulateEdge as CasticulateEdgeOriginal,
  useRecipeCookMutation,
  useCastIdQuery,
  useCasticulateClearStateMutation,
  useCasticulateSetStateMutation,
  useCasticulateStateQuery,
  useCasticulateViewScreenLazyQuery,
} from "../../../generated/graphql";
import { CamelCasedType } from "../../../helpers/components";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LastPageContext } from "../ViewResourceV2";
import { useErrorHandler } from "react-error-boundary";
import { devLog } from "../../../App";
import { stat } from "fs";
import { Button, Icon } from "../../../atoms/components";

export type CasticulateNode = CamelCasedType<CasticulateNodeOriginal>;
export type CasticulateEdge = CamelCasedType<CasticulateEdgeOriginal>;

export const SlideContext = React.createContext<{
  slideStates: SlideState[];
  appendSlideStates: (incomingSlideState: SlideState) => void;
  handleRerun: () => void;
  currentSlideToDisplayId: string;
  handleSlideJumping: (id: string) => void;
  progressBar: CasticulateNode[];
  lastVisitedTitlePageId: string;
  resultsMaxScore: number;
  resultsRawScore: number;
}>({
  slideStates: [],
  appendSlideStates: () => {},
  handleRerun: () => {},
  currentSlideToDisplayId: "",
  handleSlideJumping: () => {},
  progressBar: [{}],
  lastVisitedTitlePageId: "",
  resultsMaxScore: 0,
  resultsRawScore: 0,
});

export type SlideState = {
  slideUuid: string;
  carouselSlideState?: {
    index?: number;
  };
  assessmentSlideState?: {
    activityType: string;
    hasAnswered?: boolean | null;
    answer?: string;
    answers?: string[];
    answersInText?: string[];
    isCorrect?: boolean | null;
    response?: string;
    groupId?: string;
    correctnessArray?: {
      id: string;
      isCorrect: boolean;
    }[];
  };
  pdfSlideState?: {
    images: string[] | null;
  };
};

export const useSlideContext = () => {
  useContext(SlideContext);
};

export const titilliumFontStyle = {
  fontFamily: "'Titillium Web', sans-serif",
};

export interface CasticulateProps {
  casticulateUuid: string;
  cookRecipes?: {
    cookAsCompletedInsideCourse: () => void;
    cookAttemptInsideCourse: () => void;
    cookAsCompletedOutsideCourse: () => void;
    cookAttemptOutsideCourse: () => void;
  };
  manualEndResource?: ({
    skiplog,
    skipModal,
  }: {
    skiplog: boolean;
    skipModal: boolean;
  }) => void;
}

export function Casticulate({
  casticulateUuid,
  cookRecipes,
  manualEndResource,
}: CasticulateProps) {
  const [openCompleteModal, setOpenCompleteModal] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const courseUuid = searchParams.get("course_uuid");
  const courseBlockUuid = searchParams.get("course_block_uuid");
  const scheduledUuid = searchParams.get("course_schedule_uuid");
  const resourceUuid = searchParams.get("resource_uuid");
  const parentScreenName = searchParams.get("from_screen");
  const navigateHome = () => {
    if (!courseUuid) {
      if (searchParams.get("search_value")) {
        navigate(
          `/screen/search?search_value=${searchParams.get("search_value")}`
        );
      } else if (
        searchParams.get("search_by_tag") &&
        searchParams.get("tag_name")
      ) {
        navigate(
          `/screen/search?search_by_tag=true&tag_name=${searchParams.get(
            "tag_name"
          )}`
        );
      } else if (searchParams.get("path")) {
        navigate(
          `/directoryviewer?&path=${searchParams.get(
            "path"
          )}&base_path=${searchParams.get(
            "base_path"
          )}&from_directory=${searchParams.get(
            "from_directory"
          )}&from_screen=${parentScreenName}`
        );
      } else {
        navigate(`/screen/${parentScreenName}`);
      }
    } else {
      navigate(
        `/courseviewer?course_uuid=${searchParams.get(
          "course_uuid"
        )}&from_screen=${parentScreenName}${
          parentScreenName === "search"
            ? `&search_value=${searchParams.get("search_value")}`
            : ""
        }${
          searchParams.get("path")
            ? `&path=${searchParams.get("path")}&base_path=${searchParams.get(
                "base_path"
              )}&from_directory=${searchParams.get("from_directory")}`
            : ""
        }`
      );
    }
  };

  const lastPageContext = useContext(LastPageContext);

  const loggerFlag = false;
  const [
    getCasticulate,
    { data, loading, error, refetch: refetchCasticulate },
  ] = useCasticulateViewScreenLazyQuery({
    variables: {
      ids: {
        casticulate_uuid: casticulateUuid,
      },
    },
    fetchPolicy: "no-cache",
  });

  useErrorHandler(error);

  const castIdQuery = useCastIdQuery();

  const allNodes = data?.casticulateViewScreen?.casticulateGraph?.nodes;
  const [path, setPath] = useState<string[]>([]);

  const [titleSlides, setTitleSlides] = useState<
    { id: string; data: any; path: string[] }[]
  >([]);

  const [possibleRoute, setPossibleRoute] = useState<CasticulateNode[]>([{}]);

  useEffect(() => {
    setPossibleRoute(
      (data?.casticulateViewScreen?.casticulateGraph
        ?.possibleRoutes?.[0] as CasticulateNode[]) || [{}]
    );
  }, [data]);

  const [lastVisitedTitlePage, setLastVisitedTitlePage] = useState<string>(
    data?.casticulateViewScreen?.casticulateGraph?.possibleRoutes?.[0]?.[0]
      ?.id || ""
  );

  // useEffect(() => {
  //   const newTitleSlides = titleSlides.filter((titleSlide) =>
  //     path.includes(titleSlide.id)
  //   );
  //   setTitleSlides(newTitleSlides);
  // }, [path]);

  const [currentSlideToDisplayId, setCurrentSlideToDisplayId] =
    useState<string>("");

  useEffect(() => {
    setCurrentSlideToDisplayId(
      data?.casticulateViewScreen?.casticulateGraph?.edges?.find(
        (edge) => edge?.fromNode?.slideType === "START"
      )?.fromNode?.id || ""
    );
  }, [data]);

  const [foundNode, setFoundNode] = useState<any>();

  useEffect(() => {
    if (
      !allNodes?.find((node) => node?.id === currentSlideToDisplayId) &&
      currentSlideToDisplayId
    ) {
      console.log("Node not found");
      setStateDecisionVisible(false);
      clearCasticulateState();
    }

    setFoundNode(
      allNodes?.find((node) => node?.id === currentSlideToDisplayId)
    );
  }, [currentSlideToDisplayId]);

  function renderFoundNode() {
    const slideType = foundNode?.slideType;

    // if (slideType !== "END" && slideType !== "EXTERNAL_LINK") {
    //   lastPageContext.handleIsSlideLastPage(false);
    // }

    if (slideType === "IMAGE") {
      return (
        <CasticulateImageResource key={foundNode?.id} node={foundNode || {}} />
      );
    }

    if (slideType === "PPST") {
      return <CasticulatePPST key={foundNode?.id} node={foundNode || {}} />;
    }

    if (slideType === "VIDEO") {
      return (
        <CasticulateVideoResource key={foundNode?.id} node={foundNode || {}} />
      );
    }

    if (slideType === "AUDIO") {
      return (
        <CasticulateAudioResource key={foundNode?.id} node={foundNode || {}} />
      );
    }

    if (slideType === "PDF") {
      return (
        <CasticulatePdfResource key={foundNode?.id} node={foundNode || {}} />
      );
    }

    if (slideType === "START") {
      return (
        <CasticulateTitleSlide key={foundNode?.id} node={foundNode || {}} />
      );
    }
    if (slideType === "IMAGE_CAROUSEL")
      if (foundNode?.data.uris)
        return (
          <CasticulateImageCarousel
            key={foundNode?.id}
            node={foundNode || {}}
          />
        );
    if (slideType === "TITLE") {
      return (
        <CasticulateNavSlide
          key={foundNode?.id}
          node={foundNode || {}}
          titleSlides={titleSlides}
        />
      );
    }
    if (slideType === "EXTERNAL_LINK") {
      return (
        <CasticulateExternalLink
          key={foundNode?.id}
          node={foundNode || {}}
          cookRecipes={cookRecipes}
        />
      );
    }
    if (slideType === "END") {
      lastPageContext.handleIsSlideLastPage(true);
      // lastPageContext.handleTrainingIndicatorsEarned();
      return (
        <CasticulateEndSlide
          key={foundNode?.id}
          node={foundNode || {}}
          cookRecipes={cookRecipes}
          hasLogged={hasLoggedRef}
        />
      );
    }
    if (slideType === "MULTIPLE_CHOICE")
      return (
        <CasticulateAssessment key={foundNode?.id} node={foundNode || {}} />
      );
    if (slideType === "POLL")
      return (
        <CasticulateAssessmentPoll key={foundNode?.id} node={foundNode || {}} />
      );
    if (slideType === "TEXT")
      return (
        <CasticulateAssessmentLongAnswer
          key={foundNode?.id}
          node={foundNode || {}}
        />
      );
    if (slideType === "CHECKBOX")
      return (
        <CasticulateAssessmentCheckbox
          key={foundNode?.id}
          node={foundNode || {}}
        />
      );
    if (slideType === "ACTIVITY_RESULTS") {
      // lastPageContext.handleIsSlideLastPage(true);

      return (
        <CasticulateResultsSlide key={foundNode?.id} node={foundNode || {}} />
      );
    }
    if (slideType === "SURVEY")
      return <CasticulateSurvey key={foundNode?.id} node={foundNode || {}} />;
    else {
      return null;
    }
  }

  // Change possible Routes depending on foundNode value
  useEffect(() => {
    if (foundNode) {
      const isProgressNode =
        foundNode.slideType === "TITLE" ||
        foundNode.slideType === "START" ||
        foundNode.slideType === "END";

      // If found node is a title, start, or end slide
      if (isProgressNode) {
        if (
          !titleSlides.some((titleSlide) => titleSlide.id === foundNode?.id)
        ) {
          setTitleSlides((prev) => [
            ...prev,
            {
              data: foundNode?.data,
              id: foundNode?.id || "",
              path: path,
            },
          ]);
        }
      } else {
        setTitleSlides((prev) =>
          prev.filter((titleSlide) => path.includes(titleSlide.id))
        );
      }
    }
  }, [foundNode]);

  // Setting possible route
  useEffect(() => {
    const newPossibleRoute =
      data?.casticulateViewScreen?.casticulateGraph?.possibleRoutes?.find(
        (possibleRoute) => {
          const idsForThisRoute = possibleRoute?.map((route) => route?.id);
          return titleSlides.every((titleSlide) =>
            idsForThisRoute?.includes(titleSlide.id)
          );
        }
      );
    setPossibleRoute(newPossibleRoute as CasticulateNode[]);
  }, [titleSlides, foundNode]);

  const [slideStates, setSlideStates] = useState<SlideState[]>([]);

  const appendSlideStates = (incomingSlideState: SlideState) => {
    // Replace existing slide state
    let updatedSlideStates: SlideState[] = slideStates.map((slideState) => {
      if (slideState.slideUuid === incomingSlideState.slideUuid) {
        return incomingSlideState;
      }
      return slideState;
    });

    // If Slide states is empty
    if (
      updatedSlideStates.length === 0 ||
      !updatedSlideStates.some(
        (slideState) => slideState.slideUuid === incomingSlideState.slideUuid
      )
    ) {
      setSlideStates((prev) => [...updatedSlideStates, incomingSlideState]);
    } else {
      setSlideStates([...updatedSlideStates]);
    }
  };

  const [resultRawScore, setResultRawScore] = useState<number>(0);
  const [resultMaxScore, setResultMaxScore] = useState<number>(0);

  useEffect(() => {
    if (slideStates.length > 0 && foundNode) {
      const slideStateToUse = slideStates.filter(
        (slideStates) => slideStates.assessmentSlideState?.groupId
      );

      setResultRawScore(
        slideStateToUse
          .filter(
            (state) =>
              (state.assessmentSlideState?.activityType === "MULTIPLE_CHOICE" &&
                state.assessmentSlideState.groupId ===
                  camelcaseKeys(foundNode?.data).groupId) ||
              (state.assessmentSlideState?.activityType === "CHECKBOX" &&
                state.assessmentSlideState.groupId ===
                  camelcaseKeys(foundNode?.data).groupId)
          )
          .filter(
            (assessmentSlideState) =>
              assessmentSlideState.assessmentSlideState?.isCorrect
          ).length
      );

      setResultMaxScore(
        slideStateToUse.filter(
          (state) =>
            (state.assessmentSlideState?.activityType === "MULTIPLE_CHOICE" &&
              state.assessmentSlideState.groupId ===
                camelcaseKeys(foundNode?.data).groupId) ||
            (state.assessmentSlideState?.activityType === "CHECKBOX" &&
              state.assessmentSlideState.groupId ===
                camelcaseKeys(foundNode?.data).groupId)
        ).length
      );
    }
  }, [currentSlideToDisplayId]);

  useEffect(() => {
    // Fetch Material Icons
    const materialIconsLink = document.createElement("link");
    materialIconsLink.rel = "stylesheet";
    materialIconsLink.href =
      "https://fonts.googleapis.com/icon?family=Material+Icons";
    // Add font-display property to control loading behavior
    materialIconsLink.setAttribute("media", "all");
    materialIconsLink.setAttribute("onload", "this.media='all'");
    document.head.appendChild(materialIconsLink);

    const titilliumWebLink = document.createElement("link");
    titilliumWebLink.rel = "stylesheet";
    titilliumWebLink.href =
      "https://fonts.googleapis.com/css2?family=Titillium+Web:ital,wght@0,200;0,300;0,400;0,600;0,700;0,900;1,200;1,300;1,400;1,600;1,700&display=swap";
    document.head.appendChild(titilliumWebLink);

    return () => {
      // Cleanup: remove the link elements when the component is unmounted
      // document.head.removeChild(tailwindLink);
      document.head.removeChild(materialIconsLink);
      document.head.removeChild(titilliumWebLink);
    };
  }, []);

  const [rerunNav, setRerunNav] = useState<boolean>(false);

  // This is to trigger
  function handleRerun() {
    setRerunNav((prev) => !prev);
  }

  const [nodeScreenDuration, setNodeScreenDuration] = useState(1);
  const [cookMutation] = useRecipeCookMutation();
  const nodeScreenDurationRef = useRef(1);

  // Accumulated duration spent on the slide
  useEffect(() => {
    const screenDurationTimer = setTimeout(() => {
      if (!loading && data) {
        const currentDuration = nodeScreenDuration + 1;
        setNodeScreenDuration(currentDuration);
        nodeScreenDurationRef.current = currentDuration;
      }
    }, 1000);

    return () => {
      clearTimeout(screenDurationTimer); // Clear the setTimeout
    };
  }, [data, loading, foundNode, nodeScreenDuration]);

  // Resets node screen duration
  function clearNodeScreenDuration() {
    setNodeScreenDuration(1);
    nodeScreenDurationRef.current = 1;
  }

  function cookCasticulateAction(
    slideType: string,
    nodeScreenDuration: number
  ) {
    const activitySlides = ["MULTIPLE_CHOICE", "CHECKBOX", "POLL", "SURVEY"];
    if (!activitySlides.includes(slideType)) {
      // Non Activity Cook Mutation
      cookMutation({
        variables: {
          ingredient: {
            recipe_name: "casticulate_slide",
            body: {
              verb: "experienced",
              timestamp: new Date().toISOString(),
              object: {
                casticulate_node_id: foundNode?.id || "",
                slide_type: foundNode?.slideType || "",
                // activity_type: foundNode?.slideType || "",
              },
              result: {
                seconds: nodeScreenDuration,
                duration: nodeScreenDuration,
              },
              context: {
                resource_id: resourceUuid || "",
                casticulate_id: casticulateUuid || "",
                course_id: courseUuid || "",
                block_id: courseBlockUuid || "",
                cast_id: castIdQuery.data?.browserTitleBar?.id || "",
                domain: "https://" + window.location.host || "",
                registration: scheduledUuid || courseUuid || "",
              },
            },
          },
        },
        onCompleted: () => {
          clearNodeScreenDuration();
        },
      });
    } else {
      const activitySlideToCook = slideStates.find(
        (slideStates) => slideStates.slideUuid === foundNode?.id
      );

      // ACTIVITY COOK MUTATION
      cookMutation({
        variables: {
          ingredient: {
            recipe_name: "casticulate_activity",
            body: {
              verb: "experienced",
              timestamp: new Date().toISOString(),
              object: {
                casticulate_node_id: foundNode?.id || "",
                // slide_type: foundNode?.slideType || "",
                activity_type: foundNode?.slideType || "",
              },
              result: {
                score: activitySlideToCook?.assessmentSlideState?.isCorrect
                  ? "1"
                  : "0",
                seconds: nodeScreenDuration,
                duration: nodeScreenDuration,
                response:
                  activitySlideToCook?.assessmentSlideState?.answersInText?.join(
                    ","
                  ),
              },
              context: {
                resource_id: resourceUuid || "",
                casticulate_id: casticulateUuid || "",
                course_id: courseUuid || "",
                block_id: courseBlockUuid || "",
                cast_id: castIdQuery.data?.browserTitleBar?.id || "",
                domain: "https://" + window.location.host || "",
                registration: scheduledUuid || courseUuid || "",
              },
            },
          },
        },
        onCompleted: () => {
          clearNodeScreenDuration();
        },
      });
    }
  }

  function handleNextSlideIndex(event: Event) {
    cookCasticulateAction(
      foundNode?.slideType || "",
      nodeScreenDurationRef.current
    );
    const triggerId =
      (event.currentTarget as HTMLElement).getAttribute("data-trigger-id") ||
      "";
    const setFoundNode = (edgeToUse: CasticulateEdge) => {
      if (foundNode?.slideType !== "END") {
        setCurrentSlideToDisplayId((prev) => edgeToUse?.toNode?.id || "");
        setPath((prev) => [...prev, edgeToUse?.fromNode?.id || ""]);
      } else {
        lastPageContext.handleTrainingIndicatorsEarned();
      }
    };

    let currentSlideState = slideStates.find(
      (slideState) => slideState.slideUuid === currentSlideToDisplayId
    );

    let edgeToUse;
    let edges = data?.casticulateViewScreen?.casticulateGraph?.edges;
    if (triggerId === "") {
      edgeToUse =
        edges
          ?.filter((edge) => {
            return edge?.triggers?.some(
              (trigger) => trigger?.refId === currentSlideState?.slideUuid
            );
          })
          .filter((acceptedEdges) => {
            // Pag merong isa na wala sa triggers
            let flag = true;

            // If length of triggers and answers is not the same, auto not match
            if (
              acceptedEdges?.triggers?.length !==
              currentSlideState?.assessmentSlideState?.answers?.length
            ) {
              return false;
            }
            acceptedEdges?.triggers?.forEach((trigger) => {
              // If something is not present in tri
              if (
                trigger?.value &&
                !currentSlideState?.assessmentSlideState?.answers?.includes(
                  trigger?.value
                )
              ) {
                flag = false;
              }
            });

            return flag;
          })
          .find((edge) => {
            return edge?.fromNode?.id === currentSlideToDisplayId;
          }) ||
        data?.casticulateViewScreen?.casticulateGraph?.edges?.find(
          (edge) =>
            edge?.fromNode?.id === currentSlideToDisplayId &&
            // Check if the answer is the same slide state and trigger field WIP
            edge.triggers?.some(
              (trigger) =>
                trigger?.refId === currentSlideToDisplayId &&
                trigger?.action === "CONTINUE"
            )
        ) ||
        data?.casticulateViewScreen?.casticulateGraph?.edges?.find(
          (edge) => edge?.fromNode?.id === currentSlideToDisplayId
        );
    } else {
      edgeToUse = edges?.find((edge) =>
        edge?.triggers?.some((trigger) => trigger?.id === triggerId)
      );
    }
    setFoundNode(edgeToUse || {});
    // setCasticulateState();
  }

  async function handlePreviousSlideIndex() {
    console.log("Previous Slide");
    await setCurrentSlideToDisplayId(path?.[path.length - 1] || "");
    const newPath = [...path];
    newPath.pop();
    await setPath(newPath);
    // setCasticulateState();
  }

  function handleSlideJumping(id: string) {
    setPath(titleSlides.find((titleSlide) => titleSlide.id === id)?.path || []);
    setCurrentSlideToDisplayId(id);
  }

  const hasLoggedRef = useRef(false); // Shared flag

  useEffect(() => {
    // Automatic Resource End Mutation
    if (foundNode?.isEndNode && manualEndResource) {
      lastPageContext.handleIsSlideLastPage(true);
      manualEndResource({ skiplog: hasLoggedRef.current, skipModal: true }); // Perform logging
      hasLoggedRef.current = true; // Set the flag to true
    }

    const handleManualEnd = () => {
      if (manualEndResource && !hasLoggedRef.current) {
        lastPageContext.handleIsSlideLastPage(true);
        manualEndResource({ skiplog: false, skipModal: false }); // Perform logging
        hasLoggedRef.current = true; // Set the flag to true
      } else if (manualEndResource) {
        manualEndResource({ skiplog: true, skipModal: false }); // Skip logging if already logged
      }
    };

    const handleDownloadClick = () => {
      handleManualEnd();
    };

    const handleFinishClick = () => {
      handleManualEnd();
    };

    const nextButtons = document.querySelectorAll("#nextButton");
    const backButton = document.getElementById("backButton");
    const downloadButton = document.getElementById("downloadButton");
    const finishButton = document.getElementById("finishButton");

    if (finishButton) {
      lastPageContext.handleIsSlideLastPage(true);
    } else {
      lastPageContext.handleIsSlideLastPage(false);
    }

    downloadButton?.addEventListener("click", handleDownloadClick);
    finishButton?.addEventListener("click", handleFinishClick);
    nextButtons.forEach((nextButton) => {
      nextButton?.addEventListener("click", handleNextSlideIndex);
    });
    backButton?.addEventListener("click", handlePreviousSlideIndex);

    if (foundNode?.slideType === "START") {
      backButton?.classList.remove("flex");
      backButton?.classList.add("hidden");
    } else {
      backButton?.classList.add("flex");
      backButton?.classList.remove("hidden");
    }

    return () => {
      backButton?.removeEventListener("click", handlePreviousSlideIndex);
      nextButtons.forEach((nextButton) => {
        nextButton?.removeEventListener("click", handleNextSlideIndex);
      });
      downloadButton?.removeEventListener("click", handleDownloadClick);
      finishButton?.removeEventListener("click", handleFinishClick);
    };
  }, [currentSlideToDisplayId, rerunNav, slideStates]);

  // Last title slide visited
  useEffect(() => {
    // Getting and setting last visited title page
    const titleSlidesIds = titleSlides.map((titleSlide) => {
      return titleSlide.id;
    });

    let tempLastVisitedTitlePageId = lastVisitedTitlePage;

    if (
      path.length !== 0 &&
      currentSlideToDisplayId !== tempLastVisitedTitlePageId
    ) {
      while (!path.includes(tempLastVisitedTitlePageId)) {
        titleSlidesIds.pop();
        tempLastVisitedTitlePageId =
          titleSlidesIds?.[titleSlidesIds.length - 1] || "";
      }
    }
    setLastVisitedTitlePage((prev) => tempLastVisitedTitlePageId);

    if (
      foundNode?.slideType === "START" ||
      foundNode?.slideType === "END" ||
      foundNode?.slideType === "TITLE"
    ) {
      setLastVisitedTitlePage((prev) => foundNode?.id || "");
    }

    handleRerun();
  }, [
    currentSlideToDisplayId,
    slideStates,
    path,
    lastVisitedTitlePage,
    possibleRoute,
    foundNode,
    data,
  ]);

  // console.log({
  //   slideStates,
  //   appendSlideStates,
  //   handleRerun,
  //   currentSlideToDisplayId: currentSlideToDisplayId,
  //   handleSlideJumping,
  //   progressBar: possibleRoute,
  //   lastVisitedTitlePageId: lastVisitedTitlePage,
  //   resultsMaxScore: resultMaxScore,
  //   resultsRawScore: resultRawScore,
  // });

  const stateMemo = useMemo(
    () => ({
      slideStates: slideStates.map((slideState) => {
        const newSlideState = {
          ...slideState,
          pdfSlideState: { images: null },
        };

        return newSlideState;
      }),
      currentSlideToDisplayId: currentSlideToDisplayId,
      progressBar: possibleRoute,
      lastVisitedTitlePageId: lastVisitedTitlePage,
      resultsMaxScore: resultMaxScore,
      resultsRawScore: resultRawScore,
      path: path,
      titleSlides: titleSlides,
    }),
    [
      slideStates,
      currentSlideToDisplayId,
      possibleRoute,
      lastVisitedTitlePage,
      resultMaxScore,
      resultRawScore,
      path,
      titleSlides,
    ]
  );

  const ids = {
    casticulate_uuid: casticulateUuid,
    course_schedule_uuid: scheduledUuid,
    course_uuid: courseUuid,
    resource_uuid: resourceUuid,
  };

  const [clearCasticulateState] = useCasticulateClearStateMutation({
    variables: {
      ids: ids,
    },
    onCompleted: () => {
      refetchCasticulate({
        ids: {
          casticulate_uuid: casticulateUuid,
        },
      }).then(() => {
        setSlideStates([]);
        setCurrentSlideToDisplayId(
          data?.casticulateViewScreen?.casticulateGraph?.edges?.find(
            (edge) => edge?.fromNode?.slideType === "START"
          )?.fromNode?.id || ""
        );
        setPossibleRoute([{}]);
        setLastVisitedTitlePage(
          data?.casticulateViewScreen?.casticulateGraph
            ?.possibleRoutes?.[0]?.[0]?.id || ""
        );
        setResultRawScore(0);
        setResultMaxScore(0);
        setTitleSlides([]);
        setPath([]);
      });
    },
  });

  const [setCasticulateState] = useCasticulateSetStateMutation({
    variables: {
      ids: ids,
      data: stateMemo,
    },
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      devLog("Set Casticulate State");
      console.log(data.casticulate_set_state?.data);
      handleRerun();
    },
  });

  const [stateDecisionVisible, setStateDecisionVisible] =
    useState<boolean>(true);

  const {
    data: dataState,
    loading: loadingState,
    error: errorState,
  } = useCasticulateStateQuery({
    variables: {
      ids: ids,
    },
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      const hasState =
        data.casticulateViewScreen?.casticulateState?.hasState || false;
      devLog("Has State Condition: " + hasState);
      console.log(
        "This is state condition ",
        data.casticulateViewScreen?.casticulateState?.data
      );
      if (hasState) {
        devLog("Display state decision (Continue or Restart)");

        // Essentially loading things immediately
        getCasticulate({
          variables: {
            ids: {
              resource_uuid: resourceUuid,
              casticulate_uuid: casticulateUuid,
              course_schedule_uuid: scheduledUuid,
              course_uuid: courseUuid,
            },
          },
          onCompleted: (data) => {
            const parsedData =
              data?.casticulateViewScreen?.casticulateState?.data;

            console.log("==", data?.casticulateViewScreen?.casticulateState);
            if (parsedData && !loggerFlag) {
              setSlideStates(parsedData.slideStates);
              setCurrentSlideToDisplayId(parsedData.currentSlideToDisplayId);
              setPossibleRoute(parsedData.progressBar);
              setLastVisitedTitlePage(parsedData.lastVisitedTitlePageId);
              setResultRawScore(parsedData.resultsRawScore);
              setResultMaxScore(parsedData.resultsMaxScore);
              setTitleSlides(parsedData.titleSlides);
              setPath(parsedData.path);
            }
            console.log("Saved state", parsedData);
            console.log("TRIGGERED STATE INITIALIZATION");
            // handleRerun();
          },
        });
      } else {
        devLog("No state found, display start page");
        setStateDecisionVisible(false);

        getCasticulate({
          variables: {
            ids: {
              resource_uuid: resourceUuid,
              casticulate_uuid: casticulateUuid,
              course_schedule_uuid: scheduledUuid,
              course_uuid: courseUuid,
            },
          },
        });
      }
    },
  });

  useEffect(() => {
    if (!stateDecisionVisible) {
      setCasticulateState();
      devLog("USE EFFECT BY CURRENT SLIDE TO DISPLAY ID");
    }
  }, [currentSlideToDisplayId]);

  const onDecisionClick = (restart: boolean) => {
    if (restart) {
      clearCasticulateState();
    }

    setStateDecisionVisible(false);
  };

  return (
    <div className="w-full h-[94dvh] ">
      <Modal
        isOpen={stateDecisionVisible && !loadingState}
        // onRequestClose={() => setStateDecisionVisible(false)}
        disableCloseButton={true}
      >
        <div className="flex flex-col items-center justify-center w-full ">
          <div className="flex flex-col items-center justify-center space-y-4 w-fit">
            <h1 className="text-2xl font-black ">Continue your progress?</h1>

            <div className="pb-4 my-2 text-sm text-center">
              <p>
                Click “CONTINUE” to keep your progress and resume from where you
                left off.
              </p>
              {/* <p>Continuing this resource will keep your current progress.</p>
              <p>
                Restarting will reset your score, and previous scores cannot be
                restored.
              </p> */}
            </div>
            <div className="flex justify-center w-full mx-auto space-x-4">
              <Button
                onClick={() => onDecisionClick(true)}
                color="secondaryOutline"
              >
                <Icon name="replay" className="mr-2" /> Start Over
              </Button>
              <Button onClick={() => onDecisionClick(false)} color="secondary">
                Continue
              </Button>
            </div>
          </div>
        </div>
      </Modal>
      {/* <pre>{JSON.stringify(currentSlideToDisplayId, null, 4)}</pre> */}
      <SlideContext.Provider
        value={{
          slideStates,
          appendSlideStates,
          handleRerun,
          currentSlideToDisplayId: currentSlideToDisplayId,
          handleSlideJumping,
          progressBar: possibleRoute,
          lastVisitedTitlePageId: lastVisitedTitlePage,
          resultsMaxScore: resultMaxScore,
          resultsRawScore: resultRawScore,
        }}
      >
        <ResourceCompleteModal
          isOpen={openCompleteModal}
          onRequestClose={() => {
            setOpenCompleteModal(false);
          }}
          onBackToMainPage={() => {
            setOpenCompleteModal(false);
            navigateHome();
          }}
        />
        <div className="h-full ">{renderFoundNode()}</div>
      </SlideContext.Provider>
    </div>
  );
}
